import {
    ApiResponse,
    CreateReportPayload,
    CustomerStatementTimelineData,
    GetReportsResponse,
    GetServiceRequestPayload,
    MeterReplacementStatusRequest,
    MeterReplacementTimelineResponse,
    NewServiceFormResponse,
    NewServiceProgressReport,
    NewServiceRequestPayload,
    ReportFilter,
    ReportIssuePayload,
    RequestChangeOfDetailsPayload,
    RequestMeterTransferPayload,
    RequestStatementPayload,
    RequestTemporaryDisconnectionPayload,
    ServiceDetails,
    ServiceRequestTimelineData,
    UserDetails,
    Report,
} from '@/types'
import { AxiosClient } from '../utils/clients'

export default class ReportRepository {
    private BASE_URL = 'https://api-ecg-reports.gov-gh.com'

    constructor(private client: AxiosClient) {}

    async getReports(filters: Partial<ReportFilter>) {
        const res = await this.client.get<GetReportsResponse>(`${this.BASE_URL}/customerreports`, {
            params: filters,
        })

        return res.data
    }

    async createReport(payload: CreateReportPayload) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports`, payload)

        return res.data
    }

    async reportIssue(payload: ReportIssuePayload) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/issue`, payload)

        return res.data
    }

    async fillNewServiceRequestForm(code: string, payload: NewServiceRequestPayload) {
        const res = await this.client.post<{
            data: ApiResponse<NewServiceFormResponse>
        }>(
            `${this.BASE_URL}/customerreports/public/new-service/${code}`,
            {
                ...(payload && {
                    ...Object.fromEntries(Object.entries(payload).filter(([_, value]) => value !== '')),
                }),
            },
            {
                headers: {
                    Authorization: null,
                },
            },
        )
        return res.data
    }

    async requestNewService(payload: NewServiceRequestPayload) {
        const res = await this.client.post<ReportIssuePayload>(`${this.BASE_URL}/customerreports/new-service`, {
            ...(payload && {
                ...Object.fromEntries(Object.entries(payload).filter(([_, value]) => value !== '')),
            }),
        })
        return res.data
    }

    async requestChangeOfDetails(payload: RequestChangeOfDetailsPayload, paymentReferenceId?: string) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/change-of-account-details/${paymentReferenceId}`, {
            ...payload,
            ownerInformation: {
                ...(payload.ownerInformation && {
                    ...Object.fromEntries(
                        Object.entries(payload.ownerInformation).filter(([_, value]) => value !== ''),
                    ),
                }),
            },
            personalInformation: {
                ...(payload.personalInformation && {
                    ...Object.fromEntries(
                        Object.entries(payload.personalInformation).filter(([_, value]) => value !== ''),
                    ),
                }),
            },
        })

        return res.data
    }

    async requestStatement(payload: RequestStatementPayload) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/customer-statement`, payload)

        return res.data
    }

    async requestMeterTransfer(payload: RequestMeterTransferPayload) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/meter-transfer`, payload)

        return res.data
    }

    async requestTemporaryDisconnection(payload: RequestTemporaryDisconnectionPayload) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/temporary-disconnection`, payload)

        return res.data
    }

    async requestReconnection(payload: { accountNumber: string }) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/reconnection-request`, payload)

        return res.data
    }

    async reportDelayedInstallation(payload: { accountNumber: string }) {
        const res = await this.client.post<{
            data: ApiResponse<Report>
        }>(`${this.BASE_URL}/customerreports/delayed-installation`, payload)

        return res.data
    }

    async getServicRequestDetails(payload: GetServiceRequestPayload) {
        const res = await this.client.get<ApiResponse<ServiceDetails>>(
            `${this.BASE_URL}/customerreports/${payload.code}/service-progress`,
        )
        return res.data
    }

    async persistServicRequestDetails(payload: GetServiceRequestPayload) {
        const res = await this.client.get<ApiResponse<ServiceDetails>>(
            `${this.BASE_URL}/customerreports/${payload.code}/add-new-service`,
        )
        return res.data
    }

    async getNewServiceProgressReport(requestCode: string) {
        const res = await this.client.get<ApiResponse<NewServiceProgressReport>>(
            `${this.BASE_URL}/customerreports/${requestCode}/new-service-progress`,
        )
        return res.data
    }
    async validateNewServiceRequestFormToken(code: string) {
        const res = await this.client.get<ApiResponse<UserDetails>>(
            `${this.BASE_URL}/customerreports/new-service/${code}/validation`,
            {
                headers: {
                    Authorization: null,
                },
            },
        )
        return res.data
    }

    async getServiceRequestTimeline(trackingCode: string) {
        const res = await this.client.get<ApiResponse<ServiceRequestTimelineData>>(
            `${this.BASE_URL}/customerreports/timeline/${trackingCode}`,
        )
        return res.data
    }

    async getCustomerStatementTimeline(trackingCode: string) {
        const res = await this.client.get<ApiResponse<CustomerStatementTimelineData>>(
            `${this.BASE_URL}/customerstatement/timeline/${trackingCode}`,
        )
        return res.data
    }

    async getCustomerStatementUrls({
        trackingCode,
        paymentRef,
    }: {
        trackingCode?: string | null
        paymentRef?: string | null
    }) {
        const params = new URLSearchParams()

        if (trackingCode) {
            params.append('trackingCode', trackingCode)
        }

        if (paymentRef) {
            params.append('paymentReferenceId', paymentRef)
        }

        const queryString = params.toString()
        const url = `${this.BASE_URL}/customerstatement/get-statement-urls${queryString ? '?' + queryString : ''}`

        const res = await this.client.get<ApiResponse<{ pdfUrl: string; previewImageUrl: string | null }>>(url)
        return res.data
    }

    async reportDelayedRefundForStatement(payload: { paymentReferenceId?: string; trackingCode?: string }) {
        const { paymentReferenceId: paymentRef, trackingCode } = payload

        const newPayload: typeof payload = {}

        if (trackingCode) {
            newPayload.trackingCode = trackingCode
        }

        if (paymentRef) {
            newPayload.paymentReferenceId = paymentRef
        }

        const res = await this.client.post<ApiResponse<{ data: {} }>>(
            `${this.BASE_URL}/customerstatement/report-delayed-refund`,
            newPayload,
        )

        return res.data
    }

    async checkMeterReplacementStatus(payload: MeterReplacementStatusRequest) {
        const { trackingCode, meterNumber, meterId, accountNumber, phoneNumber } = payload
        const params = new URLSearchParams()

        if (trackingCode) {
            params.append('TrackingCode', trackingCode)
        }

        if (meterNumber) {
            params.append('MeterNumber', meterNumber)
        }

        if (accountNumber) {
            params.append('AccountNumber', accountNumber)
        }

        if (phoneNumber) {
            params.append('PhoneNumber', phoneNumber)
        }

        if (meterId) {
            params.append('MeterId', meterId)
        }

        const queryString = params.toString()
        const url = `${this.BASE_URL}/customerreports/check-replacement-status${queryString ? '?' + queryString : ''}`

        const res = await this.client.get<ApiResponse<MeterReplacementTimelineResponse>>(url)

        return res.data
    }
}
